import { AxiosError, AxiosResponse } from 'axios';
import { API_BASE_URL, apiClient, redirectToLogin, refreshAccessToken, tokenStorage } from './apiClient';
import { parseSseChunk } from '../utils/sse';
import {
  ToDoItem,
  CreateToDoItem,
  Category,
  CreateCategory,
  TimeLogEntry,
  CreateTimeLogEntry,
  LoginCredentials,
  RegisterCredentials,
  AuthResponse,
  AuthUser,
  SocialProvider,
  SocialLoginPayload,
  ChatThread,
  SendChatMessagePayload,
  SendChatMessageResponse,
  SendChatMessageResult,
  ChatUsage,
  ChatStreamEvent,
} from '../types/api';

// Authentication
export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const { data } = await apiClient.post<AuthResponse>('/auth/login/', credentials);
    tokenStorage.set(data.access, data.refresh);
    return data;
  },

  register: async (credentials: RegisterCredentials): Promise<AuthResponse> => {
    const { data } = await apiClient.post<AuthResponse>('/auth/registration/', credentials);
    tokenStorage.set(data.access, data.refresh);
    return data;
  },

  socialLogin: async (provider: SocialProvider, payload: SocialLoginPayload): Promise<AuthResponse> => {
    const { data } = await apiClient.post<AuthResponse>(`/auth/${provider}/`, payload);
    tokenStorage.set(data.access, data.refresh);
    return data;
  },

  me: async (): Promise<AuthUser> => {
    const { data } = await apiClient.get<AuthUser>('/auth/user/');
    return data;
  },

  updateProfile: async (changes: { full_name: string }): Promise<AuthUser> => {
    const { data } = await apiClient.patch<AuthUser>('/auth/user/', changes);
    return data;
  },

  deleteAccount: async (confirm: string): Promise<void> => {
    await apiClient.post('/auth/account/delete/', { confirm });
    tokenStorage.clear();
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout/', { refresh: tokenStorage.getRefresh() });
    } finally {
      tokenStorage.clear();
    }
  },
};

// To-dos
export const todoApi = {
  getAll: async (): Promise<ToDoItem[]> => {
    const { data } = await apiClient.get<ToDoItem[]>('/todo/todos/');
    return data;
  },

  getById: async (id: number): Promise<ToDoItem> => {
    const { data } = await apiClient.get<ToDoItem>(`/todo/todos/${id}/`);
    return data;
  },

  create: async (todo: CreateToDoItem): Promise<ToDoItem> => {
    const { data } = await apiClient.post<ToDoItem>('/todo/todos/', todo);
    return data;
  },

  update: async (id: number, todo: Partial<CreateToDoItem>): Promise<ToDoItem> => {
    const { data } = await apiClient.patch<ToDoItem>(`/todo/todos/${id}/`, todo);
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/todo/todos/${id}/`);
  },
};

// Categories
export const categoryApi = {
  getAll: async (): Promise<Category[]> => {
    const { data } = await apiClient.get<Category[]>('/todo/categories/');
    return data;
  },

  create: async (category: CreateCategory): Promise<Category> => {
    const { data } = await apiClient.post<Category>('/todo/categories/', category);
    return data;
  },

  update: async (id: number, category: Partial<CreateCategory>): Promise<Category> => {
    const { data } = await apiClient.patch<Category>(`/todo/categories/${id}/`, category);
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/todo/categories/${id}/`);
  },
};

// Time logs
export const timeLogApi = {
  getAll: async (): Promise<TimeLogEntry[]> => {
    const { data } = await apiClient.get<TimeLogEntry[]>('/time-logger/entries/');
    return data;
  },

  getById: async (id: number): Promise<TimeLogEntry> => {
    const { data } = await apiClient.get<TimeLogEntry>(`/time-logger/entries/${id}/`);
    return data;
  },

  create: async (entry: CreateTimeLogEntry): Promise<TimeLogEntry> => {
    const { data } = await apiClient.post<TimeLogEntry>('/time-logger/entries/', entry);
    return data;
  },

  update: async (id: number, entry: Partial<CreateTimeLogEntry>): Promise<TimeLogEntry> => {
    const { data } = await apiClient.patch<TimeLogEntry>(`/time-logger/entries/${id}/`, entry);
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/time-logger/entries/${id}/`);
  },
};

// AI chat

/**
 * The assistant failed after it started replying (an `error` event, or the
 * stream ended early). The message was received, so it stays in the thread.
 */
export class ChatStreamError extends Error {
  constructor(
    message: string,
    public code: string,
    /** set once the server has assigned the conversation */
    public threadId: number | null,
    /** the agent called tools before failing, so app data may have changed */
    public toolCalled: boolean
  ) {
    super(message);
    this.name = 'ChatStreamError';
    Object.setPrototypeOf(this, ChatStreamError.prototype); // ES5 target: keep instanceof working
  }
}

/** A non-2xx fetch response as an AxiosError, so getApiErrorMessage reads it like any API error. */
async function fetchError(res: Response): Promise<AxiosError> {
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    // not JSON (e.g. a proxy's HTML error page)
  }
  const response = { status: res.status, statusText: res.statusText, data, headers: {}, config: {} };
  return new AxiosError(
    `Request failed with status code ${res.status}`,
    String(res.status),
    undefined,
    undefined,
    response as AxiosResponse
  );
}

/** POST with the stored access token, refreshing it once on 401 like apiClient does. */
async function authorizedPost(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
  const post = (token: string | null) =>
    fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // JSON too: refusals before the stream starts are JSON, and DRF answers
        // 406 to an Accept header none of its renderers match
        Accept: 'text/event-stream, application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });

  let res: Response;
  try {
    res = await post(tokenStorage.getAccess());
    if (res.status === 401) {
      let access: string;
      try {
        access = await refreshAccessToken();
      } catch {
        redirectToLogin();
        throw await fetchError(res);
      }
      res = await post(access);
    }
  } catch (err) {
    if (err instanceof AxiosError || (err as Error)?.name === 'AbortError') throw err;
    // fetch rejects only on network failure; no response reads as "cannot reach the server"
    throw new AxiosError('Network Error', AxiosError.ERR_NETWORK);
  }
  if (!res.ok) throw await fetchError(res);
  return res;
}

export const chatApi = {
  listThreads: async (): Promise<ChatThread[]> => {
    const { data } = await apiClient.get<ChatThread[]>('/ai/chat/');
    return data;
  },

  getThread: async (threadId: number): Promise<ChatThread> => {
    const { data } = await apiClient.get<ChatThread>(`/ai/chat/${threadId}/`);
    return data;
  },

  sendMessage: async (payload: SendChatMessagePayload): Promise<SendChatMessageResult> => {
    const { data } = await apiClient.post<SendChatMessageResponse>('/ai/chat/', payload);
    return data.response;
  },

  /**
   * Send a message and get the reply as it's written: `onEvent` sees each token
   * and tool call. Resolves with the `done` payload (same shape as sendMessage).
   * Rejects with an AxiosError if the message is refused before the agent runs
   * (validation, quota, scope guard), or a ChatStreamError if it fails mid-reply.
   * Aborting `signal` stops reading; the server still finishes the turn.
   */
  streamMessage: async (
    payload: SendChatMessagePayload,
    onEvent: (event: ChatStreamEvent) => void,
    signal?: AbortSignal
  ): Promise<SendChatMessageResult> => {
    const res = await authorizedPost('/ai/chat/', { ...payload, stream: true }, signal);
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let threadId: number | null = null;
    let toolCalled = false;

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const parsed = parseSseChunk(buffer + decoder.decode(value, { stream: true }));
        buffer = parsed.rest;
        for (const raw of parsed.events) {
          const event = { event: raw.event, data: JSON.parse(raw.data) } as ChatStreamEvent;
          if (event.event === 'thread') threadId = event.data.thread_id;
          if (event.event === 'tool_call') toolCalled = true;
          if (event.event === 'error') {
            throw new ChatStreamError(event.data.error, event.data.code, threadId, toolCalled);
          }
          onEvent(event);
          if (event.event === 'done') return event.data;
        }
      }
    } catch (err) {
      if (err instanceof ChatStreamError || (err as Error)?.name === 'AbortError') throw err;
      // the connection dropped mid-reply
    } finally {
      reader.cancel().catch(() => undefined);
    }
    throw new ChatStreamError(
      'The connection to the assistant was interrupted. Refresh the conversation to see its reply.',
      'stream_interrupted',
      threadId,
      toolCalled
    );
  },

  deleteThread: async (threadId: number): Promise<void> => {
    await apiClient.delete(`/ai/chat/${threadId}/`);
  },

  getUsage: async (): Promise<ChatUsage> => {
    const { data } = await apiClient.get<ChatUsage>('/ai/usage/');
    return data;
  },
};
